import type { ReactNode } from "react";
import { Link } from "react-router";
import { buttonClass } from "@/design/buttons";
import { BusinessProfileForm } from "@/design/BusinessProfileForm";
import { DataUnavailable } from "@/design/DataUnavailable";
import { OpeningHoursEditor } from "@/design/OpeningHoursEditor";
import {
  PractitionersManager,
  type PractitionerView,
} from "@/design/PractitionersManager";
import { ServicesManager, type ServiceView } from "@/design/ServicesManager";
import { Card, PageShell } from "@/design/ui";
import { formToJson, type FormAction } from "@/design/useFormAction";
import { businessCopyFor } from "@/modules/booking/commands/businessCopy";
import { formStateFor as catalogStateFor } from "@/modules/catalog/commands/catalogCopy";
import {
  formStateFor as schedulingStateFor,
  hoursSavedMessage,
} from "@/modules/scheduling/commands/schedulingCopy";
import type { Failure } from "@/modules/shared/failure";
import type { FormState } from "@/modules/shared/forms";
import type {
  BusinessProfileRecord,
  Category,
  ServiceSuggestion,
} from "@/modules/catalog/surfaces";
import type { OpeningHour } from "@/modules/scheduling/surfaces";
import { BusinessPage } from "~/components/BusinessChrome";
import { ScreenPending } from "~/components/ScreenPending";
import { call } from "~/lib/api";
import { deletePhotos, uploadLogo, uploadPhoto } from "~/lib/clinicMedia";
import { useQuery, type Query } from "~/lib/useQuery";

/**
 * A clinic's three configuration screens: Services, Opening hours, and the
 * public profile (R22 T14).
 *
 * All three are Manager-only (H26) and all three are the same shape: read once,
 * hand the shared component a command, re-read when it succeeds. They live in
 * one file because they are one pattern, and separating them would mean three
 * copies of the frame below.
 */

/**
 * Turn a shared component's `FormData` into an API request.
 *
 * **This is the whole adapter for every business form.** `useFormAction` kept
 * `FormData` as the payload precisely so this could be one function rather than
 * one per screen: the field names the components already use become the JSON
 * keys, and a repeated key (the hours grid) becomes an array.
 *
 * The refusal comes back as a `FormState` through whichever copy map the screen
 * names, so the sentence is the one the Next host renders too.
 */
export function apiFormAction<T>({
  method,
  path,
  render,
  onSuccess,
  success,
}: {
  method: "POST" | "PUT" | "DELETE";
  path: (form: FormData) => string;
  /** The module's own copy map, so the sentence is the server adapter's. */
  render: (failure: Failure) => FormState;
  onSuccess?: () => void;
  /** The confirmation, from the reply when it says something worth reading. */
  success?: (value: T) => string;
}): FormAction {
  return async (form) => {
    const result = await call<T>(path(form), {
      method,
      body: formToJson(form),
    });
    if (!result.ok) return render(result.failure);
    onSuccess?.();
    return { success: success ? success(result.value) : "saved" };
  };
}

/** The frame the three share: the way back, a title, and the three read states. */
function ToolScreen<T>({
  title,
  intro,
  what,
  query,
  width = "max-w-md",
  children,
}: {
  /**
   * Omitted when the component supplies its own heading (the profile editor
   * does). Rendering an empty `<h1>` above it is not merely untidy: a screen
   * reader announces the empty one first and a document outline shows two
   * headings where there is one screen.
   */
  title?: string;
  intro?: string;
  /** What could not be read, for the failure panel's sentence. */
  what: string;
  query: Query<T>;
  width?: string;
  children: (data: T) => ReactNode;
}) {
  return (
    <BusinessPage>
      <PageShell>
        <div className={`mx-auto w-full ${width}`}>
          <Link
            to="/business"
            className="mb-4 inline-block text-sm font-medium text-accent"
          >
            ← Back to your business
          </Link>
          {title && (
            <h1 className="mb-1 text-xl font-bold text-ink">{title}</h1>
          )}
          {intro ? (
            <p className="mb-6 text-sm text-body">{intro}</p>
          ) : (
            <div className="mb-6" />
          )}

          {query.loading && !query.data ? (
            <ScreenPending />
          ) : query.failure || !query.data ? (
            <DataUnavailable
              configured={query.failure !== "not_configured"}
              what={what}
            />
          ) : (
            children(query.data)
          )}
        </div>
      </PageShell>
    </BusinessPage>
  );
}

// ── Services (docs/ux/screens/ServicesScreen.md) ─────────────────────────────

interface ServicesPayload {
  services: ServiceView[];
  suggestions: ServiceSuggestion[];
}

export function ServicesScreen() {
  const services = useQuery<ServicesPayload>("/business/services");
  const reload = () => services.reload();

  return (
    <ToolScreen title="Services" what="your services" query={services}>
      {(data) => (
        <ServicesManager
          services={data.services}
          suggestions={data.suggestions}
          commands={{
            add: apiFormAction({
              method: "POST",
              path: () => "/business/services",
              render: catalogStateFor,
              onSuccess: reload,
            }),
            edit: apiFormAction({
              method: "PUT",
              path: (form) =>
                `/business/services/${String(form.get("serviceId") ?? "")}`,
              render: catalogStateFor,
              onSuccess: reload,
            }),
            remove: apiFormAction({
              method: "DELETE",
              path: (form) =>
                `/business/services/${String(form.get("serviceId") ?? "")}`,
              render: catalogStateFor,
              onSuccess: reload,
            }),
          }}
        />
      )}
    </ToolScreen>
  );
}

// ── The team (docs/ux/screens/PractitionersScreen.md) ────────────────────────

interface PractitionersPayload {
  unlocked: boolean;
  practitioners: PractitionerView[];
}

/**
 * **The tools gate is a state of this screen, not a redirect** (J2#2): a clinic
 * with no Service has nobody to manage yet, and the panel says which one thing
 * to do first. Sending them away instead would leave them guessing.
 */
export function PractitionersScreen() {
  const team = useQuery<PractitionersPayload>("/business/practitioners");
  const reload = () => team.reload();

  return (
    <ToolScreen title="Practitioners" what="your team" query={team}>
      {(data) =>
        !data.unlocked ? (
          <ToolsLocked what="practitioner management" />
        ) : (
          <PractitionersManager
            practitioners={data.practitioners}
            commands={{
              add: apiFormAction({
                method: "POST",
                path: () => "/business/practitioners",
                render: catalogStateFor,
                onSuccess: reload,
              }),
              archive: apiFormAction({
                method: "POST",
                path: (form) =>
                  `/business/practitioners/${seat(form)}/archive`,
                render: catalogStateFor,
                onSuccess: reload,
              }),
              setTier: apiFormAction({
                method: "PUT",
                path: (form) => `/business/practitioners/${seat(form)}/tier`,
                render: catalogStateFor,
                onSuccess: reload,
              }),
              setServiceLocations: apiFormAction({
                method: "PUT",
                path: (form) =>
                  `/business/practitioners/${seat(form)}/service-locations`,
                render: catalogStateFor,
                onSuccess: reload,
              }),
              clearBookings: apiFormAction({
                method: "POST",
                path: (form) =>
                  `/business/practitioners/${seat(form)}/clear-bookings`,
                // Booking owns this refusal, so Booking's words say it.
                render: (failure) => ({ formError: businessCopyFor(failure) }),
                onSuccess: reload,
              }),
            }}
          />
        )
      }
    </ToolScreen>
  );
}

/** The hidden field every practitioner row posts, as a path segment. */
const seat = (form: FormData) => String(form.get("practitionerId") ?? "");

/**
 * The tools gate (J2#2), shared by the two screens that have one.
 *
 * It names the single next action rather than explaining the rule twice, which
 * is what "add your first service" is doing on both of them.
 */
function ToolsLocked({ what }: { what: string }) {
  return (
    <div className="rounded-xl border border-line bg-surface p-6 shadow-sm">
      <h2 className="mb-1 font-bold text-ink">
        Add your first service to unlock {what}.
      </h2>
      <p className="mb-4 text-sm text-body">
        Your business tools unlock once at least one service exists.
      </p>
      <Link
        to="/business/services"
        className={buttonClass("primary", "inline-block")}
      >
        Add your first service
      </Link>
    </div>
  );
}

// ── Opening hours (docs/ux/screens/ClinicOpeningHoursScreen.md) ──────────────

interface HoursPayload {
  unlocked: boolean;
  hours: OpeningHour[];
}

export function ClinicOpeningHoursScreen() {
  const hours = useQuery<HoursPayload>("/business/hours");

  return (
    <ToolScreen
      title="Opening hours"
      intro="Tick the hours your clinic is open. This is the calendar for everybody who works here; time off for one person is recorded on their own page."
      what="your opening hours"
      query={hours}
      width="max-w-2xl"
    >
      {(data) =>
        !data.unlocked ? (
          <ToolsLocked what="your opening hours" />
        ) : (
          <div className="rounded-xl border border-line bg-surface p-5 shadow-sm">
            <OpeningHoursEditor
              saved={data.hours}
              save={apiFormAction<{
                summary: { days: number; hours: number };
              }>({
                method: "PUT",
                path: () => "/business/hours",
                render: schedulingStateFor,
                onSuccess: () => hours.reload(),
                // The counts come back rather than a sentence, so the client
                // says "open 5 days, 40 hours a week" in its own words. That
                // is the message worth reading: not that it saved, but what
                // the clinic just opened.
                success: (value) => hoursSavedMessage(value.summary),
              })}
            />
          </div>
        )
      }
    </ToolScreen>
  );
}

// ── The public profile (docs/ux/screens/BusinessProfileEditScreen.md) ────────

interface ProfilePayload {
  profile: BusinessProfileRecord;
  categories: Category[];
}

export function BusinessProfileEditScreen() {
  const profile = useQuery<ProfilePayload>("/business/profile");

  return (
    <ToolScreen what="your public profile" query={profile}>
      {(data) => (
        <Card>
          <BusinessProfileForm
            profile={data.profile}
            categories={data.categories}
            save={saveProfileAction(data.profile, () => profile.reload())}
          />
        </Card>
      )}
    </ToolScreen>
  );
}

/**
 * The profile save, which unlike every other business form has files in it
 * (BusinessProfileEditScreen element 8, H59).
 *
 * **Upload, then save, then tidy.** The new logo and photos go to storage
 * first, as the member; only when every one is there does the save run, with
 * their addresses. If any upload fails, the ones that did are deleted and
 * nothing is saved (unhappy 6 and 6f), so a half-uploaded list never reaches
 * a Consumer. Photos the Manager removed are deleted only after the save
 * succeeds, because until then the clinic still shows them.
 */
function saveProfileAction(
  current: BusinessProfileRecord,
  reload: () => void,
): FormAction {
  return async (form) => {
    const entries = form.getAll("photo");
    const logo = form.get("logo");
    const body = formToJson(form);
    delete body.photo;
    delete body.logo;

    const uploaded: string[] = [];
    const photoUrls: string[] = [];
    for (const entry of entries) {
      if (typeof entry === "string") {
        photoUrls.push(entry);
        continue;
      }
      const result = await uploadPhoto(current.id, entry);
      if (!result.ok) {
        await deletePhotos(uploaded);
        return { formError: "The photos couldn't be uploaded, so nothing was changed. Try again." };
      }
      uploaded.push(result.url);
      photoUrls.push(result.url);
    }

    let logoUrl: string | undefined;
    if (logo instanceof File && logo.size > 0) {
      const result = await uploadLogo(current.id, logo);
      if (!result.ok) {
        await deletePhotos(uploaded);
        return { formError: "The logo couldn't be uploaded, so nothing was changed. Try again." };
      }
      logoUrl = result.url;
    }

    const result = await call("/business/profile", {
      method: "PUT",
      body: { ...body, photoUrls, ...(logoUrl ? { logoUrl } : {}) },
    });
    if (!result.ok) {
      await deletePhotos(uploaded);
      return catalogStateFor(result.failure);
    }

    await deletePhotos(current.photoUrls.filter((url) => !photoUrls.includes(url)));
    reload();
    return { success: "Your profile has been updated." };
  };
}
